import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Button, Caption1, MessageBar, MessageBarBody, Text, makeStyles } from '@fluentui/react-components';
import type { Person } from '@vigeo/shared';
import { PopupDialog, tokens } from '@vigeo/ui';
import { PortalLinkReveal } from './PortalLinkReveal.js';
import { usePortalLinkIssue } from './portalLinkIssue.js';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

/**
 * Handing a person their portal link and access code by hand: the one dialog, the same on every screen (#501, PG5).
 *
 * The state is the shared per-person store (`usePortalLinkIssue`); this adds only what one PLACEMENT needs: whether
 * its own dialog is up. The keyboard goes back to the button that opened it: `PopupDialog` does it, told which button by `opener`. The link state is shared,
 * so `issued` alone would also open a dialog for a link another placement minted; the dialog is for the one the
 * button here opened.
 *
 * With `askFirst` the button opens a question (the link replaces any live one). Without it the click makes the
 * link at once, which is what the not-emailed notice does: its own sentence beside the button already says so.
 * Either way the link and code appear in the same result dialog, which closes only with "Done, hide them".
 */
export function usePortalLinkHandOver(person: Person, askFirst: boolean) {
  const link = usePortalLinkIssue(person.id);
  const [open, setOpen] = useState(false);
  /** The button that opened it: the question and the result are two dialogs, so the second cannot learn it from the keyboard. */
  const opener = useRef<HTMLElement | null>(null);
  const busy = link.pending && open;
  // A placement that makes the link at once has no dialog to show when that fails, so it is no longer "open"
  // and cannot be opened later by a link some other placement makes.
  useEffect(() => { if (open && !askFirst && !link.pending && link.issued === null) setOpen(false); }, [open, askFirst, link.pending, link.issued]);
  return {
    ...link,
    askFirst,
    open,
    /** The question is showing: nothing has been made, or it is being made, or it failed. */
    asking: open && askFirst && link.issued === null,
    /** The link and code are showing, in this placement's dialog. */
    showing: open && link.issued !== null,
    busy,
    opener,
    start: (event?: MouseEvent<HTMLElement>) => {
      opener.current = event?.currentTarget ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      setOpen(true);
      if (!askFirst) link.issue();
    },
    /** Cancel, the X, Esc and a click outside: all one thing, and refused while the link is being made. */
    cancel: () => { if (busy) return; link.clear(); setOpen(false); },
    /** The confirmed click, and Try again. The only thing that makes a link from a placement that asks first. */
    make: () => link.issue(),
    /** Done, hide them: the link and code leave the screen, the dialog closes, the button has the focus again. */
    done: () => { link.clear(); setOpen(false); },
  };
}

export type PortalLinkHandOver = ReturnType<typeof usePortalLinkHandOver>;

/** The button that opens it. Off while the link is on screen, so a second click cannot replace the one just copied. */
export function PortalLinkButton({ handOver, appearance = 'primary' }: { readonly handOver: PortalLinkHandOver; readonly appearance?: 'primary' | 'subtle' }) {
  return (
    <Button appearance={appearance} disabled={handOver.pending || handOver.issued !== null || handOver.asking} onClick={handOver.start}>
      {handOver.pending ? 'Getting the link…' : 'Get the link and code'}
    </Button>
  );
}

/**
 * The question (small, 480), the wait and the failure in one dialog, then the link and access code (small, 480), both on
 * `PopupDialog`. The question has an X, Esc and a click outside, which do what Cancel does, until the link is being made; the
 * result has none of them and closes only with "Done, hide them".
 */
export function PortalLinkDialog({ handOver, firstName }: { readonly handOver: PortalLinkHandOver; readonly firstName: string }) {
  const styles = useStyles();
  const { asking, showing, busy } = handOver;

  if (showing && handOver.issued !== null) {
    return (
      <PopupDialog
        open
        title="Link and access code"
        dismissible={false}
        cancelLabel={null}
        startOn="primary"
        opener={handOver.opener}
        onClose={handOver.done}
        primary={{ label: 'Done, hide them', onClick: handOver.done }}
      >
        <div className={styles.body}>
          <Caption1>Give {firstName} both. A new link replaces any link {firstName} already had.</Caption1>
          <PortalLinkReveal issued={handOver.issued} />
        </div>
      </PopupDialog>
    );
  }

  const failed = !busy && handOver.errorMessage !== null;
  return (
    <PopupDialog
      open={asking}
      title={`Make a link and code for ${firstName}?`}
      startOn="cancel"
      opener={handOver.opener}
      busy={busy}
      onClose={handOver.cancel}
      primary={{ label: failed ? 'Try again' : 'Make a new link', busyLabel: 'Making the link…', onClick: handOver.make }}
    >
      <div className={styles.body}>
        {failed ? <MessageBar intent="error"><MessageBarBody>{handOver.errorMessage}</MessageBarBody></MessageBar> : (
          <Text>
            Making a new link replaces any link {firstName} already has. If {firstName} is signing right now, that link stops working.
            You will see the new link and code once, so be ready to copy them.
          </Text>
        )}
      </div>
    </PopupDialog>
  );
}
